/**
 * The TypeScript twins of `scripts/stack_analysis.py`'s two CSV writers, so
 * the app can export the same numbers without a round trip to the worker.
 *
 * **Byte-identity with the Python is the deliverable**, and it is pinned
 * mechanically: `__tests__/csv.test.ts` scrapes both column tuples out of the
 * Python source and asserts both writers reproduce the committed fixtures
 * under `scripts/test_fixtures/layer_stack/` exactly. A hand-copied table
 * drifts; this project has recorded seven failures of that shape.
 *
 * Every number goes through `pyFixed` — the existing mirror of Python's
 * round-then-test rule, negative-zero guard included. A second number
 * formatter would reintroduce a divergence that once produced 2,482
 * mismatches in 30,000 values, invisible to inspection.
 *
 * Two known, deliberately-unfixed divergences from the Python, both
 * unreachable through `JSON.parse` of a real worker document: `n()` prints
 * `''` for a non-finite figure where Python's `_csv_num` prints the literal
 * `nan` (JSON has no NaN/Infinity literal), and `result.rollUp ?? ''` prints
 * `''` where Python's `str(result.get('rollUp', ''))` would print `'None'`
 * for an explicit JSON `null` (the worker never emits one; `rollUp` is
 * always a real verdict string).
 */
import type { Criterion } from '@/components/VerdictChip';
import {
  STATION_SECTIONS_CROSSFALL_CSV_COLUMNS,
  crossfallCells,
  pyFixed,
} from '@/lib/stationVolumes/stationExport';
import type { StackBlock, StackResult } from '@/lib/stationVolumes/types';

/**
 * One row per LAYER: the compliance table as data. The measurement columns
 * describe the layer's THICKNESS block; the three elevation/thickness verdict
 * columns keep the other two questions visible without duplicating figures.
 *
 * `not_measured_pct` here is against the **UNION GRID** — 100 % minus the
 * layer's `coverage` criterion, whose denominator is every cell of the one
 * grid spanning every declared surface. The identically named column in
 * `STACK_STATION_CSV_COLUMNS` is against the **CORRIDOR**. Both are right for
 * their scope, and the two files DO NOT JOIN on this column.
 */
export const STACK_LAYER_CSV_COLUMNS = [
  'layer_i',
  'layer_name',
  'level_from',
  'level_to',
  'state',
  'mean_thickness_m',
  'design_thickness_m',
  'deviation_m',
  'under_area_pct',
  'in_spec_area_pct',
  'over_area_pct',
  'not_measured_pct',
  'deficit_volume_m3',
  'thickness_verdict',
  'elevation_top_verdict',
  'elevation_bottom_verdict',
  'layer_verdict',
] as const;

/**
 * Layer identity, station identity, then the SAME NINE expectation columns the
 * shipped per-station CSV appends (`STATION_EXPECTATION_CSV_COLUMNS`), by the
 * same names, so the two exports read alike.
 *
 * `not_measured_pct` here is against the **CORRIDOR** — 100 % minus the bin's
 * `coveragePct`, whose denominator is `binCellCount`. Same column name, a
 * different denominator from the layer CSV's, deliberately: a per-bin figure
 * against the site-wide grid would be a near-zero percentage on every bin.
 */
export const STACK_STATION_CSV_COLUMNS = [
  'layer_i',
  'layer_name',
  'level_from',
  'level_to',
  'sta_start_display',
  'sta_end_display',
  'sta_start_internal',
  'sta_end_internal',
  'mean_thickness_m',
  'design_thickness_m',
  'deviation_m',
  'under_area_pct',
  'in_spec_area_pct',
  'over_area_pct',
  'not_measured_pct',
  'deficit_volume_m3',
  'verdict',
] as const;

/**
 * The eight CROSSFALL columns, APPENDED to the seventeen above and only on a
 * document that carried a check (crossfall spec §5) — so a run that asked for
 * none produces exactly the file it always has, byte for byte.
 *
 * NOT a list of its own: `stack_analysis.py::_crossfall_columns` returns
 * `station_bins._SECTION_CROSSFALL_CSV_COLUMNS`, so this re-exports the
 * sections twin's copy of it. The two documents must name the same eight
 * things, and a second literal is a second thing to forget.
 */
export const STACK_STATION_CROSSFALL_CSV_COLUMNS = STATION_SECTIONS_CROSSFALL_CSV_COLUMNS;

/**
 * What the download is called when the run registered no byproduct to take a
 * name from — the worker's own local names (`processLayerStack/index.ts`'s
 * `STACK_LAYERS_CSV` / `STACK_STATIONS_CSV`), so a file saved from the dock
 * and a file saved from the batch page are recognisably the same document.
 */
export const STACK_LAYERS_CSV_FILENAME = 'stack_layers.csv';
export const STACK_STATIONS_CSV_FILENAME = 'stack_stations.csv';

/** `tuple.index(key)`: the position, or a loud failure — never a silent -1. */
const colIndex = (cols: readonly string[], key: string): number => {
  const i = cols.indexOf(key);
  if (i < 0) throw new Error(`column ${key} is missing from the column tuple`);
  return i;
};

/**
 * How many columns of each row are MEASUREMENT columns, i.e. the ones blanked
 * wholesale when nothing was measured. Derived from the tuples rather than
 * written as `8` twice, so adding a column cannot leave a stale count behind.
 */
const LAYER_FIGURE_COLUMNS =
  colIndex(STACK_LAYER_CSV_COLUMNS, 'thickness_verdict') -
  colIndex(STACK_LAYER_CSV_COLUMNS, 'mean_thickness_m');
const STATION_FIGURE_COLUMNS =
  colIndex(STACK_STATION_CSV_COLUMNS, 'verdict') -
  colIndex(STACK_STATION_CSV_COLUMNS, 'mean_thickness_m');

/**
 * `alignment_geom.one_line`: collapse CR/LF runs to a single space.
 *
 * Level names are user-typed. A newline left alone splits one `#` comment line
 * into two, and the second half — no longer starting with `#` — is read as a
 * data row by pandas, QGIS and Excel alike.
 */
const oneLine = (s: string): string => s.replace(/[\r\n]+/g, ' ');

/**
 * `stack_analysis.py::_csv_text` — a text field, quoted only when it has to be
 * (RFC 4180 minimal).
 *
 * Level names are user-supplied. One comma in "Sub-base, upper" silently
 * shifts every column after it, in a CONTRACTUAL artefact, and nothing
 * downstream would notice because the row still parses.
 *
 * **Order matters.** `oneLine` runs FIRST, so by the time the newline test
 * below runs there is nothing left to find — those two entries are
 * unreachable, and kept as belt and braces exactly as the Python keeps them. A
 * port that copies only the quoting rule emits a QUOTED field containing a
 * real newline where Python emits an unquoted single line: byte-different,
 * both legal CSV, so no parser complains and only a byte-identity test catches
 * it.
 */
export const csvText = (v: string | number | boolean | null | undefined): string => {
  if (v == null) return '';
  const s = oneLine(String(v));
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replaceAll('"', '""')}"`;
  }
  return s;
};

/** `station_bins.py::_csv_num`: fixed-point, blank for a missing figure. */
const n = (v: number | null | undefined, d = 4): string =>
  v == null || !Number.isFinite(v) ? '' : pyFixed(v, d);

/**
 * Python's raw `f"{v:.{d}f}"`, used only in the `#` provenance block.
 *
 * Not the same function as `n`: `_csv_num` rounds and then prints a bare `0`
 * for a rounded-away negative, while a bare format spec keeps the sign
 * (`f"{-0.0004:.3f}"` is `-0.000`). Built on `pyFixed` so there is still only
 * one rounding implementation — this only restores the sign `_csv_num`'s guard
 * strips.
 */
const fixed = (v: number, d: number): string => {
  const s = pyFixed(v, d);
  return (v < 0 || Object.is(v, -0)) && !s.startsWith('-') ? `-${s}` : s;
};

/**
 * A block's grid coverage %, recovered from its `coverage` CRITERION.
 *
 * `_judge` hands `expectation.evaluate` a coverage percentage and keeps the
 * criteria it produced, but stores no field of its own for it. Reading it back
 * off the criterion keeps the block shape exactly as ruling R11 froze it.
 */
const coveragePct = (block: StackBlock): number | null => {
  for (const c of (block.criteria ?? []) as Criterion[]) if (c.key === 'coverage') return c.actual;
  return null;
};

/**
 * The `#` provenance block both stack CSVs share.
 *
 * Short and prose-like, matching `station_bins._csv_header_lines`: pandas
 * (`comment='#'`) and QGIS's delimited-text reader skip `#` lines, but Excel
 * does NOT — it imports them as rows the reader then deletes.
 *
 * Every LEVEL is named with its as-built state, including the unsurveyed ones.
 * A compliance table whose header lists only the levels that happened to be
 * measured cannot be audited later: the reader cannot tell a three-level
 * structure two-thirds built from a two-level one finished.
 */
const stackCsvHeaderLines = (result: StackResult, generatedAtIso: string, title: string): string[] => {
  const { levels, layers } = result;
  const out = [
    `# ${title}   Generated: ${generatedAtIso}`,
    `# Structure: ${levels.length} levels, ${layers.length} layers   ` +
      `Roll-up: ${oneLine(String(result.rollUp ?? ''))}`,
  ];
  levels.forEach((lv, i) => {
    out.push(`# Level ${i + 1}: ${oneLine(String(lv.name))}   as-built: ${oneLine(String(lv.state))}`);
  });
  const { grid } = result;
  if (grid) {
    const b = grid.bounds;
    out.push(
      '# Grid: bounds ' +
        `${fixed(b[0], 3)} ${fixed(b[1], 3)} ${fixed(b[2], 3)} ${fixed(b[3], 3)}   ` +
        `resolution ${fixed(grid.effectiveResolutionM, 3)} m ` +
        `(requested ${fixed(grid.requestedResolutionM, 3)} m)   ` +
        `${grid.width} x ${grid.height} cells`,
    );
  }
  out.push(
    '# A blank measurement column means NOTHING WAS MEASURED there ' +
      '-- never a zero, which would claim a measurement that was never taken',
  );
  return out;
};

/**
 * One row per LAYER — the compliance table as data, not as a rendering.
 *
 * `design_thickness_m` is `mean_thickness_m - deviation_m`, exactly as the
 * shipped station CSV derives it, and NEVER a corridor scalar: the scalar is
 * right only for MATCH and CONSTANT, while a stack judges thickness in
 * SURFACES mode where the design varies cell to cell. The subtraction is exact
 * — `meanDeviationM` is `mean(dz) - mean(expected)`.
 *
 * **Blanks are gated on `measuredCells`, not on the state.** After ruling R11
 * every block carries a uniform key set, so a layer whose surfaces do not
 * overlap at all still holds `deficitVolumeM3: 0.0` and `underPct: 0.0` — real
 * zeros. Written straight through, that layer prints an authoritative "0.0000
 * m3 deficit, 0 % out of tolerance": a clean bill of health for a layer nobody
 * surveyed, and worse than the PENDING case because the row does not say
 * PENDING.
 *
 * The VERDICT columns are not gated: `INCONCLUSIVE` is a real, earned verdict
 * and is the one thing that tells the reader why the row is blank.
 */
export function stackLayersCsv(result: StackResult, generatedAtIso: string): string {
  const out = stackCsvHeaderLines(result, generatedAtIso, 'Layer compliance');
  out.push(STACK_LAYER_CSV_COLUMNS.join(','));

  for (const lay of result.layers) {
    const th = lay.thickness ?? null;
    let figures: string[];
    if (th != null && th.measuredCells) {
      const meanThickness = th.meanThicknessM;
      const meanDeviation = th.meanDeviationM;
      const designThickness =
        meanThickness == null || meanDeviation == null ? null : meanThickness - meanDeviation;
      const coverage = coveragePct(th);
      figures = [
        n(meanThickness),
        n(designThickness),
        n(meanDeviation),
        n(th.underPct, 1),
        n(th.inSpecPct, 1),
        n(th.overPct, 1),
        n(coverage == null ? null : 100.0 - coverage, 1),
        n(th.deficitVolumeM3),
      ];
    } else {
      figures = new Array<string>(LAYER_FIGURE_COLUMNS).fill('');
    }

    out.push(
      [
        String(lay.i),
        csvText(lay.name),
        csvText(lay.levelFrom),
        csvText(lay.levelTo),
        csvText(lay.state),
        ...figures,
        th != null ? csvText(th.verdict) : '',
        'elevationTop' in lay ? csvText(lay.elevationTop!.verdict) : '',
        'elevationBottom' in lay ? csvText(lay.elevationBottom!.verdict) : '',
        csvText(lay.verdict),
      ].join(','),
    );
  }
  return `${out.join('\n')}\n`;
}

/**
 * One row per (station bin × layer). This function COMPUTES NOTHING — the bins
 * were produced by `analyse_stack` while the rasters, the grid and the station
 * spec were still in scope (ruling R12).
 *
 * A PENDING layer contributes NO rows at all: it has no `stations` key, and a
 * row of blanks per bin for a layer nobody has built yet would bury the
 * measured ones. (The LAYER CSV keeps its PENDING row, because there the row
 * IS the statement that the layer exists and is unbuilt.)
 *
 * A bin that measured nothing blanks all its measurement columns, for the same
 * reason `stackLayersCsv` does: the empty-population stub carries
 * `underPct: 0.0` and `deficitVolumeM3: 0.0`, which read as "measured, and
 * perfect". Its `verdict` (`INCONCLUSIVE`) is kept.
 *
 * `STACK_STATION_CROSSFALL_CSV_COLUMNS` are APPENDED, and only on a document
 * where some layer carried the check. Each bin prints its START station
 * line's block; the CLOSING line of the last bin has no row of its own here,
 * the same way it has no thickness row, while `layers[i].crossfall` rolls up
 * every line — the two count different populations by design.
 */
export function stackStationsCsv(result: StackResult, generatedAtIso: string): string {
  const out = stackCsvHeaderLines(result, generatedAtIso, 'Layer thickness by station');
  // One question, asked once over the whole document: did ANY layer carry a
  // crossfall check? A per-layer header would give two different row widths in
  // one CSV, which nothing can parse. Key PRESENCE, exactly as the Python's
  // `any("crossfall" in lay ...)` asks it — a layer whose check produced a
  // summary of nothing still widens the file.
  const hasCrossfall = result.layers.some((lay) => 'crossfall' in lay);
  out.push(
    (hasCrossfall
      ? [...STACK_STATION_CSV_COLUMNS, ...STACK_STATION_CROSSFALL_CSV_COLUMNS]
      : STACK_STATION_CSV_COLUMNS
    ).join(','),
  );

  for (const lay of result.layers) {
    for (const b of lay.stations ?? []) {
      let figures: string[];
      if (b.cellCount) {
        const meanThickness = b.meanThicknessM;
        const meanDeviation = b.meanDeviationM;
        const designThickness =
          meanThickness == null || meanDeviation == null ? null : meanThickness - meanDeviation;
        figures = [
          n(meanThickness),
          n(designThickness),
          n(meanDeviation),
          n(b.underPct, 1),
          n(b.inSpecPct, 1),
          n(b.overPct, 1),
          n(100.0 - b.coveragePct, 1),
          n(b.deficitVolumeM3),
        ];
      } else {
        figures = new Array<string>(STATION_FIGURE_COLUMNS).fill('');
      }

      const row = [
        String(lay.i),
        csvText(lay.name),
        csvText(lay.levelFrom),
        csvText(lay.levelTo),
        n(b.staStartDisplayM, 2),
        n(b.staEndDisplayM, 2),
        n(b.staStartInternalM, 2),
        n(b.staEndInternalM, 2),
        ...figures,
        csvText(b.verdict),
      ];
      if (hasCrossfall) {
        // `crossfallCells` is the sections twin's — one implementation of
        // `station_bins._section_crossfall_cells` in TypeScript, exactly as
        // `stack_stations_csv` reaches across for `sb._section_crossfall_cells`
        // rather than restating it. A layer with no check of its own
        // contributes blanks, not zeros: the same rule the figure columns
        // above follow.
        //
        // The VERDICT cells go through `csvText`, the numeric ones do not.
        // Which is which is read off the COLUMN NAME rather than the position:
        // "every fourth one" would silently become the wrong cells the day a
        // fifth per-side figure is added. `station_sections.csv` does NOT
        // quote these same cells; this is the file whose neighbouring column
        // is a user-supplied level name, so it is the file that already has to
        // quote — a deliberate difference between the two documents.
        row.push(
          ...crossfallCells(b).map((c, i) =>
            STACK_STATION_CROSSFALL_CSV_COLUMNS[i].endsWith('_verdict') ? csvText(c) : c,
          ),
        );
      }
      out.push(row.join(','));
    }
  }
  return `${out.join('\n')}\n`;
}
